import { CheckboxListFilter } from 'components/SA/Filters'
import React, { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'
import styles from './CheckboxListFacet.module.scss'
import FilterFacetText from './FilterFacetText'
import {
  CustomHandleUpdateSelectedFacetParams,
  FacetOptionObject,
  FacetServiceApi,
  UseReduxSearchProviderResponse,
} from '../types'
import { getHrefFunction, updateClientSideUrl } from '../helpers/url'
import useReduxSearchProvider from '../hooks/useReduxSearchProvider'

type CheckboxFilterOptionsObject = {
  text: string
  checked: boolean
  onClick: () => void
  href: string
  isHidden?: boolean
}

type CheckboxListFacetProps = {
  baseUrl: string
  nextAppRoute: string
  facetService: FacetServiceApi
  facetName: string
  facetOptions: ReadonlyArray<FacetOptionObject>
  customFilterOptions?: ReadonlyArray<CheckboxFilterOptionsObject>
  maxDisplayedItems?: number
  searchFacetTextPlaceholder?: string
  additionalFacetsToChange?: Record<string, ReadonlyArray<string>>
  useReduxProviderHook?: () => UseReduxSearchProviderResponse
  customHandleUpdateSelectedFacet?: (params: CustomHandleUpdateSelectedFacetParams) => void
  customClickFacetOption?: (params: CustomHandleUpdateSelectedFacetParams) => void
}

const CheckboxListFacet: FC<PropsWithChildren<CheckboxListFacetProps>> = ({
  baseUrl,
  nextAppRoute,
  facetService,
  facetName,
  facetOptions,
  customFilterOptions,
  maxDisplayedItems = 6,
  searchFacetTextPlaceholder,
  additionalFacetsToChange = {},
  useReduxProviderHook = useReduxSearchProvider,
  customHandleUpdateSelectedFacet,
  customClickFacetOption,
  children,
}) => {
  const {
    selectedFacets,
    basicQueryParams,
    pathParams,
    sharedFacetNames,
    pageParams,
  } = useReduxProviderHook()

  const [facetListOptions, setFacetListOptions] = useState(facetOptions)
  const isMounted = useRef(true)

  const handleFilterFacetText = (textQuery: string) => {
    const filteredCountriesArray = facetOptions.filter(({ label }) =>
      label?.toLocaleLowerCase()?.includes(textQuery?.toLocaleLowerCase())
    )

    if (isMounted.current) {
      setFacetListOptions(filteredCountriesArray)
    }
  }

  const handleUpdateSelectedFacets = ({
    newSelectedFacets,
    newQueryParams,
    facetName,
    facetValue,
  }) => {
    const newSelectedFacetsAndQueryParams = {
      ...newSelectedFacets,
      ...newQueryParams,
      ...additionalFacetsToChange,
    }
    const href = getHrefFunction({
      facetsAndQueryParams: newSelectedFacetsAndQueryParams,
      pathParams,
      sharedFacetNames,
    })()
    const newUrl = `${baseUrl}${href}`

    if (Boolean(customHandleUpdateSelectedFacet)) {
      customHandleUpdateSelectedFacet?.({
        newSelectedFacets: newSelectedFacetsAndQueryParams,
        newUrl,
        facetName,
        facetValue,
      })
      return
    }

    customClickFacetOption?.({
      newSelectedFacets: newSelectedFacetsAndQueryParams,
      newUrl,
      facetName,
      facetValue,
    })
    updateClientSideUrl(newUrl, nextAppRoute)
  }

  const filterOptions =
    customFilterOptions ||
    facetListOptions.map(({ label, value }, index) => {
      const href = getHrefFunction({
        facetsAndQueryParams: { [facetName]: [label], page: [''] },
        pathParams,
        sharedFacetNames,
      })()
      const newHref = `${baseUrl}${href}`

      return {
        text: label,
        checked: facetService.getIsFacetSelected({
          selectedFacets,
          pageParams,
          facetValue: value,
          facetName,
        }),
        onClick: () =>
          facetService.handleClickFacet({
            facetValue: value,
            facetName,
            selectedFacets,
            basicQueryParams,
            updateSelectedFacetsAndQueryParams: handleUpdateSelectedFacets,
          }),
        isHidden: index >= maxDisplayedItems,
        href: newHref,
      }
    })

  useEffect(() => {
    setFacetListOptions(facetOptions)
  }, [facetOptions])

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  return (
    <div className={styles.facetListContainer}>
      {Boolean(searchFacetTextPlaceholder) && (
        <FilterFacetText
          onSearch={handleFilterFacetText}
          placeholder={searchFacetTextPlaceholder}
        />
      )}
      {!filterOptions.length ? (
        <p className='defaultTypographySmall'>No Options Available</p>
      ) : (
        <CheckboxListFilter filterOptions={filterOptions} facetName={facetName}>
          {children}
        </CheckboxListFilter>
      )}
    </div>
  )
}

export default CheckboxListFacet
